// Las listas válidas, traídas del backend.
//
// NO se copian acá a mano. `lib/opciones.js` del servidor es la única
// fuente de verdad; duplicarlas en el cliente sería garantizar que
// algún día digan cosas distintas y que el usuario elija un valor que
// la base rechaza.
//
// Se piden una vez y quedan en memoria. Son pocos kilobytes y no
// cambian durante la sesión.

import {
  API_URL,
  leerSesion,
  listarColores,
  listarOcasiones,
  leerPerfil,
} from "./api";
import { registrarPropios, olvidarPropios } from "./tintes";

let cache = null;
let pidiendo = null;

/* GENERACIÓN DEL CACHÉ — la carrera que tapaba una ocasión recién
 * creada (2026-08-21).
 *
 * `invalidarOpciones()` ponía `cache = null` y listo. Alcanza si no hay
 * nada en vuelo. Si SÍ lo hay —y lo hay, porque varias pantallas piden
 * opciones al montarse— pasa esto:
 *
 *   1. Una pantalla llama a cargarOpciones(). Sale el pedido.
 *   2. El usuario crea "Deporte". invalidarOpciones() borra el caché.
 *   3. El pedido del paso 1 vuelve con la lista VIEJA y hace
 *      `cache = {...}`.
 *
 * El caché queda repoblado con datos anteriores a la creación, y la
 * ocasión nueva no aparece hasta recargar la página entera. Se ve
 * exactamente igual que "no se guardó" — el mismo síntoma que ya costó
 * cuatro vueltas.
 *
 * Con un contador, un pedido que vuelve después de una invalidación
 * sabe que su resultado está vencido y no lo cachea.
 */
let generacion = 0;

// Fallback mínimo para que la interfaz no quede en blanco si el backend
// no responde. Deliberadamente incompleto: es para poder mostrar un
// error usable, no para trabajar sin servidor.
const MINIMO = {
  tipos: [],
  colores: [],
  familiasColor: {},
  materiales: [],
  categorias: [],
  estaciones: [],
  atajosEstacion: {},
  paises: [],
  estados: [],
  disponibilidades: [],
  zonasPorTipo: {},
  defaults: {},
  maxPorTanda: 30,
};

/* ------------------------------------------------------------------
   ME-8 · QUE EL SILENCIO DEJE DE SER SILENCIO (2026-09-06)

   Con el backend caído, `cargarOpciones()` devolvía MINIMO —todas las
   listas vacías— y la app pintaba selects sin una sola opción, sin
   decir nada. El usuario ve un armario que "perdió" los colores, los
   tipos y las estaciones, y concluye que se le borraron los datos.

   El aviso va en UN solo lugar y no en las seis pantallas que leen
   `opciones`. Es el mismo criterio que `registrarPropios`: si cada
   pantalla tuviera que acordarse, agregar una pantalla nueva sería
   descubrir de nuevo que el error no se ve.
------------------------------------------------------------------ */

let fallo = false;
const oyentes = new Set();

function avisarFallo(nuevo) {
  if (nuevo === fallo) return;
  fallo = nuevo;
  // Una copia: un oyente que se da de baja durante el aviso —React lo
  // hace al desmontar— modificaría el Set que se está recorriendo.
  for (const fn of [...oyentes]) fn(fallo);
}

/** Avisa cuando las listas pasan a estar caídas, o vuelven. Devuelve la
 *  función para darse de baja. */
export function suscribirFalloDeOpciones(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

export function opcionesFallaron() {
  return fallo;
}

/* ------------------------------------------------------------------
   LAS LISTAS DEL USUARIO SE MEZCLAN ACÁ

   EL BUG QUE ESTO ARREGLA (2026-08-21)

   El dueño creó una ocasión propia y no aparecía en "Qué me pongo".
   La causa: `/api/opciones` es GLOBAL y sin sesión —sirve las listas
   antes de que nadie entre— así que `categorias` traía las 8 base y
   nada más. La ocasión existía en la base, se podía marcar en una
   prenda desde el editor (que pide `/api/ocasiones` por su cuenta), y
   era invisible en todas las demás pantallas.

   O sea: la función andaba a medias, y la mitad que no andaba era la
   que le daba sentido.

   La solución es mezclar acá y no en cada pantalla. Si cada filtro
   pidiera sus propias listas, agregar una pantalla nueva significaría
   acordarse de este detalle otra vez — y no acordarse se ve como "mi
   ocasión no existe", que es indistinguible de un dato mal guardado.
------------------------------------------------------------------ */

async function misListas() {
  // Sin sesión no hay listas propias y no vale ni intentar: los dos
  // endpoints darían 401 y el usuario que todavía no entró vería dos
  // errores en la consola antes del login.
  if (!leerSesion()?.access_token) {
    olvidarPropios();
    return { colores: [], categorias: [], sembrado: false, guardarropa: null };
  }

  // Las tres en paralelo: son independientes y ponerlas en serie
  // agregaría tres viajes a cada arranque de la app.
  const [c, o, p] = await Promise.all([
    listarColores().catch(() => null),
    listarOcasiones().catch(() => null),
    leerPerfil().catch(() => null),
  ]);

  // Si una falla, se sigue con las listas base. Perder los colores
  // propios degrada la app; no poder abrirla, no.
  // Los colores propios que tienen muestra elegida se registran para
  // que el calendario y las franjas los dibujen. Va acá y no en cada
  // pantalla: si cada una tuviera que acordarse, agregar una pantalla
  // nueva sería descubrir de nuevo que los colores propios salen grises.
  registrarPropios(c?.propios || []);

  return {
    colores: (c?.propios || []).map((x) => x.nombre),
    categorias: (o?.propias || []).map((x) => x.nombre),
    // Un usuario SEMBRADO no usa la lista base: la suya la reemplaza.
    // Ver `ocasionesValidas` en lib/rutas_ocasiones.js — el servidor ya
    // decide así, y si el cliente decidiera distinto habría dos
    // opiniones sobre qué ocasiones existen.
    sembrado: !!o?.sembrado,
    // Qué ropa usa. Se trae acá y no en cada pantalla por la misma
    // razón que las ocasiones propias: si cada desplegable tuviera que
    // acordarse de pedirlo, agregar una pantalla nueva sería descubrir
    // de nuevo que la lista sale sin ordenar.
    //
    // ⚠️  ORDENA, NO FILTRA. Ver `GUARDARROPA` en lib/prendas_tipos.js.
    guardarropa: p?.perfil?.guardarropa || null,
  };
}

export async function cargarOpciones() {
  const haySesion = !!leerSesion()?.access_token;

  // EL AGUJERO QUE ESTO TAPA
  //
  // El caché es de MÓDULO: vive mientras la pestaña esté abierta. Si la
  // primera llamada ocurrió SIN sesión —la pantalla de login, un
  // componente que se adelantó, una sesión que todavía no se había
  // leído— quedó cacheada la lista base sola. Y como `if (cache) return
  // cache` no vuelve a preguntar nunca, el usuario podía entrar,
  // trabajar toda la sesión, y no ver jamás sus ocasiones propias.
  //
  // Se ve exactamente igual que "no se guardó", que es el reporte que
  // llegó. Por eso el caché ahora recuerda SI se armó con sesión: si no,
  // y ahora sí la hay, se rearma.
  if (cache && (cache.conSesion || !haySesion)) return cache;
  if (cache) {
    /* ⚠️  `generacion++` FALTABA ACÁ (ME-7, 2026-09-05).
     *
     * Este camino es "había caché sin sesión y ahora sí hay": se tira y
     * se vuelve a pedir. Pero tirarlo SIN subir la generación deja
     * pasar al pedido sessionless que todavía está en vuelo: cuando
     * vuelve, su `miGeneracion === generacion` da true y repuebla el
     * caché con la lista base, sin las ocasiones ni los colores
     * propios.
     *
     * Es EXACTAMENTE la carrera que el comentario de arriba dice haber
     * arreglado, reintroducida por la otra puerta. Y el síntoma es el
     * mismo que costó cuatro vueltas: te logueás y tus ocasiones
     * propias no aparecen hasta recargar la página. */
    generacion++;
    cache = null;
    pidiendo = null;
  }
  if (pidiendo) return pidiendo;

  const miGeneracion = generacion;

  pidiendo = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/opciones`);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const base = await res.json();

      const propias = await misListas();

      // Las propias van DESPUÉS de las base, a propósito: en cualquier
      // lista de chips o de opciones, lo que uno conoce aparece primero
      // y lo suyo al final, que es donde lo busca.
      /* EL BUG QUE ESTO ARREGLA (2026-08-21), Y LO CREÉ YO CON EL
       * SEMBRADO.
       *
       * Acá se mezclaba SIEMPRE la lista base con la del usuario. Para
       * alguien de antes está bien: la base son sus ocasiones y las
       * propias son el agregado.
       *
       * Para un usuario SEMBRADO está mal, y se vio en los datos
       * reales del dueño: sus 14 ocasiones sembradas MÁS las 8 base
       * globales daban 22 entradas con "Casual", "Trabajo",
       * "Entrecasa", "Gym", "Noche", "Verano casual", "Verano noche" y
       * "Trabajo día especial" DUPLICADAS. Un filtro con la mitad de
       * las opciones repetidas.
       *
       * El servidor ya decidía bien (`ocasionesValidas` devuelve solo
       * las propias para un sembrado). El cliente decidía distinto, y
       * dos opiniones sobre qué ocasiones existen es peor que una
       * opinión equivocada: al menos la equivocada es consistente.
       */
      /* LA TAXONOMÍA ORDENADA SEGÚN QUÉ ROPA USA
       *
       * El servidor manda las tres versiones (`taxonomias.ambos`,
       * `.femenino`, `.masculino`) y no la elegida, a propósito:
       * `/api/opciones` es GLOBAL y sin sesión, así que no sabe de quién
       * es la pregunta. Elegir acá es el único lugar donde se sabe.
       *
       * ⚠️  ORDENA, NO FILTRA. Las tres versiones tienen las MISMAS 108
       * prendas; cambia el orden dentro de cada subcategoría y el orden
       * de las subcategorías. Nadie pierde una prenda por esto — si
       * alguna vez alguien la pierde, el bug está acá o en
       * `taxonomiaPara`, no en el dato del usuario.
       *
       * Sin guardarropa —o con uno que el servidor no conoce— queda la
       * lista neutra, que es la que había antes de que esto existiera.
       */
      const taxonomia =
        base.taxonomias?.[propias.guardarropa] || base.taxonomia;

      const armado = {
        ...base,
        taxonomia,
        colores: [...(base.colores || []), ...propias.colores],
        categorias: propias.sembrado
          ? propias.categorias
          : [...(base.categorias || []), ...propias.categorias],
        // Se guarda con qué se armó, no cuántas trajo: un usuario sin
        // ocasiones propias tiene lista vacía y aun así el caché es
        // válido. Distinguir "no tiene" de "no se preguntó" es todo el
        // punto.
        conSesion: haySesion,
      };

      // Si alguien invalidó mientras esto viajaba, el resultado está
      // vencido: se devuelve —quien lo pidió necesita algo— pero NO se
      // cachea, así que la próxima pantalla vuelve a preguntar.
      if (miGeneracion === generacion) cache = armado;
      avisarFallo(false);
      return armado;
    } catch {
      // No se cachea el fallback: la próxima vez se vuelve a intentar.
      // Y se avisa: un MINIMO silencioso se ve igual que un armario
      // vacío. Ver ME-8 arriba.
      avisarFallo(true);
      return MINIMO;
    } finally {
      pidiendo = null;
    }
  })();

  return pidiendo;
}

/**
 * Tirar el caché para que la próxima pantalla vuelva a pedir.
 *
 * Se llama al crear o borrar un color o una ocasión. Sin esto, crear
 * "Misa" y cambiar de pestaña seguiría mostrando la lista vieja hasta
 * recargar la página entera — y el usuario concluiría, con razón, que
 * no se guardó.
 */
export function invalidarOpciones() {
  generacion++;
  cache = null;
  pidiendo = null;
  // Los tintes propios son de ESTE usuario. Al cerrar sesión, quien
  // entre después en la misma pestaña vería sus colores hasta que algo
  // los pise.
  olvidarPropios();
}

export function opcionesEnCache() {
  return cache || MINIMO;
}

// Zonas que cubre un tipo, para mostrarlas en la ficha. Espeja
// `zonasDe()` del servidor, pero leyendo la tabla que él mismo mandó.
export function zonasDe(tipo) {
  return opcionesEnCache().zonasPorTipo?.[tipo] || [];
}

/* ------------------------------------------------------------------
   Estaciones
------------------------------------------------------------------ */

// Los atajos ("Primavera-Verano" = {Primavera, Verano}) los manda el
// servidor en `atajosEstacion`. NO se escriben acá: si mañana se agrega
// uno, aparece solo. Duplicar la tabla sería la forma segura de que
// cliente y servidor terminen diciendo cosas distintas.

/**
 * Convierte lo que sea en un conjunto de estaciones válido.
 *
 * Acepta un array, un atajo, un valor viejo ("Todo año") o nada. Espeja
 * `normalizar()` de lib/estaciones.js, con la misma regla de oro:
 * ante la duda, las cuatro. Una prenda que aparece de más se ve y se
 * corrige; una que desaparece de todos los outfits no se nota nunca.
 */
export function expandirEstaciones(valor) {
  const o = opcionesEnCache();
  const validas = o.estaciones || [];
  const atajos = o.atajosEstacion || {};
  const todas = () => [...validas];

  if (valor === null || valor === undefined || valor === "") return todas();

  const crudas = Array.isArray(valor) ? valor : [valor];
  const limpias = [];

  for (const c of crudas) {
    for (const e of atajos[c] || [c]) {
      if (validas.includes(e) && !limpias.includes(e)) limpias.push(e);
    }
  }

  if (limpias.length === 0) return todas();

  // Ordenadas por el orden del año, que es el de `validas`.
  return validas.filter((e) => limpias.includes(e));
}

/**
 * El atajo que describe este conjunto, o null si no hay ninguno.
 *
 * Sirve para marcar cuál de los botones de atajo está activo, y para
 * mostrar "Primavera-Verano" en vez de "Primavera, Verano".
 */
export function atajoDeEstaciones(estaciones) {
  const atajos = opcionesEnCache().atajosEstacion || {};
  const firma = [...expandirEstaciones(estaciones)].sort().join("|");
  for (const [nombre, set] of Object.entries(atajos)) {
    if ([...set].sort().join("|") === firma) return nombre;
  }
  return null;
}

export function etiquetaEstaciones(estaciones) {
  return atajoDeEstaciones(estaciones) || expandirEstaciones(estaciones).join(", ");
}
